import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { createIsSomeEnum, useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserTaxEstimates } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import {
  CALCULATE_SAFE_HARBOR_STEP,
  useSafeHarborCatchUpEnabled,
} from './helpers'
import { CalculateSafeHarborIntro } from './CalculateLateHarborIntro'
import { TotalTaxesAndIncome } from './TotalTaxesAndIncome'
import { FinishCalculateSafeHarbor } from './FinishCalculateSafeHarbor'
import { QuarterlyTaxRecommendations } from './QuarterlyTaxRecommendations'
import { PriorQuarterPayments } from './PriorQuarterPayments'
import { PageHeader } from '@/components/PageHeader'
import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { selectRelevantSafeHarborDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const isStep = createIsSomeEnum(CALCULATE_SAFE_HARBOR_STEP)

export const CalculateLateHarborFlow = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })

  const stepParam = searchParams.get('step')
  const step = isStep(stepParam)
    ? stepParam
    : CALCULATE_SAFE_HARBOR_STEP.calculateSafeHarborIntro

  const details = useReselector(selectRelevantSafeHarborDetails)
  const catchUpEnabled = useSafeHarborCatchUpEnabled()

  useEffect(() => {
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  const content = useMemo(() => {
    switch (step) {
      case CALCULATE_SAFE_HARBOR_STEP.calculateSafeHarborIntro:
        return <CalculateSafeHarborIntro details={details} />
      case CALCULATE_SAFE_HARBOR_STEP.quarterlyTaxRecommendations:
        return <QuarterlyTaxRecommendations details={details} />
      case CALCULATE_SAFE_HARBOR_STEP.totalTaxesAndIncome:
        return <TotalTaxesAndIncome details={details} />
      case CALCULATE_SAFE_HARBOR_STEP.priorQuarterPayments:
        // Reachable only from a stale link once the flag is off, since the step
        // is no longer in the array the footer walks.
        return catchUpEnabled ? (
          <PriorQuarterPayments details={details} />
        ) : (
          <FinishCalculateSafeHarbor details={details} />
        )
      case CALCULATE_SAFE_HARBOR_STEP.finish:
        return <FinishCalculateSafeHarbor details={details} />
      default:
        return step satisfies never
    }
  }, [step, details, catchUpEnabled])

  if (!details) {
    return null
  }

  return (
    <>
      <span ref={scrollRef} />
      <PageHeader header="Calculate safe harbor" isMultiStep />
      {content}
    </>
  )
}
